import React, { useState, useEffect } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { updateCompetiteur } from "../../services/competiteursServices";
import { toast } from "react-toastify";
import { getCompetitionById } from "../../services/competitionsServices";

const ALL_DISCIPLINES = [
  "Judo",
  "Ne-Waza",
  "JJB",
  "Jujitsu Combat",
  "Jujitsu Duo",
  "Arbitre",
  "Commissaire Sportif",
  "Judo Adapté"
];

/** Valeur enregistrée en BDD (value) vs libellé affiché (label) */
const GRADE_OPTIONS = [
  { label: "Ceinture blanche", value: "blanche" },
  { label: "Ceinture blanche 1 liseré", value: "blanche 1 liseré" },
  { label: "Ceinture blanche 2 liserés", value: "blanche 2 liserés" },
  { label: "Ceinture blanche-jaune", value: "blanche-jaune" },
  { label: "Ceinture jaune", value: "jaune" },
  { label: "Ceinture jaune-orange", value: "jaune-orange" },
  { label: "Ceinture orange", value: "orange" },
  { label: "Ceinture orange-verte", value: "orange-verte" },
  { label: "Ceinture verte", value: "verte" },
  { label: "Ceinture verte-bleue", value: "verte-bleue" },
  { label: "Ceinture bleue", value: "bleue" },
  { label: "Ceinture bleue-marron", value: "bleue-marron" },
  { label: "Ceinture marron", value: "marron" },
  { label: "1er Dan", value: "1er Dan" },
  { label: "2e Dan", value: "2e Dan" },
  { label: "3e Dan", value: "3e Dan" },
  { label: "4e Dan", value: "4e Dan" },
  { label: "5e Dan", value: "5e Dan" },
];

const normalize = (str = "") =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

/** Couleurs du badge selon la valeur (sans “Ceinture ”) */
const getGradeClasses = (g = "") => {
  const s = normalize(g);

  if (s.includes("blanche 2 liseres"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche 1 lisere"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche"))
    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
  if (s.includes("blanche-jaune"))
    return "bg-yellow-50 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";

  if (s.includes("jaune-orange"))
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";
  if (s.includes("jaune"))
    return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 text-xs";

  if (s.includes("orange-verte"))
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 text-xs";
  if (s.includes("orange"))
    return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 text-xs";

  if (s.includes("verte-bleue"))
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 text-xs";
  if (s.includes("verte"))
    return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 text-xs";

  if (s.includes("bleue-marron"))
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 text-xs";
  if (s.includes("bleue"))
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 text-xs";

  if (s.includes("marron") || s.includes("brun"))
    return "bg-amber-900 text-amber-100 dark:bg-amber-900/40 dark:text-amber-100 text-xs";

  if (s.includes("dan"))
    return "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-xs";

  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-xs";
};

/** Essaie de convertir un ancien “Ceinture verte-bleue” → “verte-bleue” */
const coerceGradeValue = (raw = "") => {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (/^ceinture\s+/i.test(trimmed)) {
    return trimmed.replace(/^ceinture\s+/i, ""); // enlève le préfixe “Ceinture ”
  }
  return trimmed; // pour les Dan ou déjà au bon format
};

const labelFromValue = (value = "") =>
  GRADE_OPTIONS.find((o) => o.value === value)?.label || value || "Aucun grade";

const EditCompetiteurModal = ({
  isOpen,
  onClose,
  competiteur,
  refreshList,
}) => {
  const [disciplines, setDisciplines] = useState(ALL_DISCIPLINES);
  const [competiteurData, setCompetiteurData] = useState({
    nom_competiteur: "",
    prenom_competiteur: "",
    ddn_competiteur: "",
    licence_competiteur: "",
    club_competiteur: "",
    poids_competiteur: "",
    genre_competiteur: "M",
    discipline_competiteur: "Judo",
    grade_competiteur: "", // ← on stocke la “value” (ex: “verte-bleue”)
  });

  useEffect(() => {
    if (isOpen && competiteur) {
      setCompetiteurData({
        nom_competiteur: competiteur.nom_competiteur || "",
        prenom_competiteur: competiteur.prenom_competiteur || "",
        ddn_competiteur: (competiteur.ddn_competiteur || "").slice(0, 10),
        licence_competiteur: competiteur.licence_competiteur || "",
        club_competiteur: competiteur.club_competiteur || "",
        poids_competiteur: competiteur.poids_competiteur ?? "",
        genre_competiteur: competiteur.genre_competiteur || "M",
        discipline_competiteur:
          competiteur.discipline_competiteur &&
          ALL_DISCIPLINES.includes(competiteur.discipline_competiteur)
            ? competiteur.discipline_competiteur
            : "Judo",
        grade_competiteur: coerceGradeValue(
          competiteur.grade_competiteur || "",
        ),
      });
    }
  }, [isOpen, competiteur]);

  useEffect(() => {
    const fetchDisciplines = async () => {
      if (!isOpen || !competiteur) return;
      try {
        const competitionData = await getCompetitionById(
          competiteur.competition_id,
        );
        const raw = competitionData?.disciplines_competition;
        const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;

        const allowed =
          Array.isArray(parsed) && parsed.length > 0
            ? parsed.filter((d) => ALL_DISCIPLINES.includes(d))
            : [];

        setDisciplines(allowed.length > 0 ? allowed : ALL_DISCIPLINES);

        setCompetiteurData((prev) => {
          const current = prev.discipline_competiteur || "Judo";
          const base = allowed.length > 0 ? allowed : ALL_DISCIPLINES;
          return {
            ...prev,
            discipline_competiteur: base.includes(current) ? current : base[0],
          };
        });
      } catch {
        setDisciplines(ALL_DISCIPLINES);
      }
    };
    fetchDisciplines();
  }, [isOpen, competiteur]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompetiteurData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...competiteurData,
        discipline_competiteur: disciplines.includes(
          competiteurData.discipline_competiteur,
        )
          ? competiteurData.discipline_competiteur
          : disciplines[0] || "Judo",
        grade_competiteur: coerceGradeValue(competiteurData.grade_competiteur), // ← on envoie la value sans “Ceinture ”
      };

      await updateCompetiteur(competiteur.id, payload);
      toast.success("Compétiteur mis à jour avec succès");
      refreshList?.();
      onClose?.();
    } catch {
      toast.error("Erreur lors de la mise à jour du compétiteur");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-3"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-2xl rounded-2xl border border-gray-200/70 dark:border-slate-700/70 bg-white/80 dark:bg-slate-900/80 backdrop-blur shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 bg-primary text-white rounded-t-2xl">
          <h3 className="text-lg font-semibold">Éditer un compétiteur</h3>
          <button
            aria-label="Fermer"
            className="h-8 w-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            onClick={onClose}
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div>
            <label className="block text-sm font-medium mb-1">Nom</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="nom_competiteur"
              value={competiteurData.nom_competiteur}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Prénom</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="prenom_competiteur"
              value={competiteurData.prenom_competiteur}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Date de naissance
            </label>
            <input
              type="date"
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="ddn_competiteur"
              value={competiteurData.ddn_competiteur}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Licence</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="licence_competiteur"
              value={competiteurData.licence_competiteur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Club</label>
            <input
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="club_competiteur"
              value={competiteurData.club_competiteur}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Poids</label>
            <input
              type="number"
              step="0.1"
              min="0"
              placeholder="ex: 24.0"
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              name="poids_competiteur"
              value={competiteurData.poids_competiteur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Genre</label>
            <select
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
              name="genre_competiteur"
              value={competiteurData.genre_competiteur}
              onChange={handleChange}
              required
            >
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Discipline</label>
            <select
              className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
              name="discipline_competiteur"
              value={competiteurData.discipline_competiteur}
              onChange={handleChange}
              required
            >
              {disciplines.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          {/* Grade (value stockée, label affiché) */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium mb-1">Grade</label>
            <div className="flex items-center gap-3">
              <select
                className="w-full sm:w-2/3 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-black"
                name="grade_competiteur"
                value={competiteurData.grade_competiteur}
                onChange={handleChange}
              >
                <option value="">—</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>

              <span
                className={`inline-flex items-center rounded-full px-3 py-1 ${getGradeClasses(
                  competiteurData.grade_competiteur,
                )}`}
              >
                {labelFromValue(competiteurData.grade_competiteur)}
              </span>
            </div>
          </div>

          <div className="sm:col-span-2 mt-2 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              text="Fermer"
              icon={FaTimes}
              onClick={onClose}
              className="gap-2 rounded-lg"
            />
            <Button
              text="Mettre à jour"
              icon={FaSave}
              type="submit"
              className="gap-2 rounded-lg"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompetiteurModal;
